export const PREVIEW_PREPARE_DEADLINE_MS = 30_000;
export const PREVIEW_FIRST_FRAME_MS = 10_000;
export const PREVIEW_LOST_PROGRESS_MS = 5_000;

/** Add-scoped preview observation state. Never a Continue input. */
export type PreviewAvailability = 'idle' | 'preparing' | 'receiving' | 'unavailable';

export const R8_PREVIEW_COPY = (seconds: number): string =>
  `Preparing the camera preview did not finish within ${seconds} seconds. Retry the preview.`;

export const R9_PREVIEW_COPY =
  'Iris could not prepare this preview because a service request failed. Retry the preview.';

export const R11_NDI_COPY =
  'Preview unavailable for this NDI source. Check that the source is sending video, then choose Retry preview.';

export const R11_RTSP_COPY =
  'Preview unavailable for this camera. Check the stream address and that the camera is sending video, then choose Retry preview.';

/**
 * Shown while Retry is disabled because the raw setup for this attempt has not
 * settled yet. Continue eligibility lives entirely in the source/video footers,
 * so this surface never promises an action it cannot verify.
 */
export const PREVIEW_PENDING_COPY =
  'Iris is still finishing this preview setup. Retry becomes available once the current attempt settles. You can close this dialog at any time.';

export const R14_PREVIEW_COPY = (reference: string): string =>
  `Iris could not complete this step. Your settings are still here. Try again. If it continues, contact support with reference ${reference}.`;

/** Boundary the failure was observed at. */
export type PreviewPrepareOperation = 'auth-access-token' | 'media-token' | 'preview-prepare';

/** Typed classification of a preview token/setup rejection. */
export type PreviewPrepareOutcome = {
  /** Fixed safe copy. Never interpolates a cause message. */
  readonly copy: string;

  /** Boundary the cause chain actually proves, not merely the declared one. */
  readonly operation: PreviewPrepareOperation;

  /** True only for evidenced transport failures. */
  readonly transient: boolean;

  /** The owned presentation budget elapsed; raw IO may still be pending. */
  readonly timedOut?: boolean;

  /** Allowlisted status code for diagnostics, when the chain carries one. */
  readonly status?: number | undefined;
};

/** Status codes safe to retain from ApiError or a protobuf error envelope. */
const SAFE_STATUS = new Set([400, 401, 403, 404, 408, 409, 422, 429, 500, 502, 503, 504]);

/** Inspect the actual StudioError → ApiError → transport/envelope cause chain. */
export const classifyPreviewPrepareCause = (
  cause: unknown,
  operation: PreviewPrepareOperation,
  reference: string
): PreviewPrepareOutcome => {
  let resolved = operation;
  let status: number | undefined;
  let transport = false;
  let nonTransient = false;
  const seen = new Set<unknown>();
  // Bound both malformed cycles and unusually deep third-party error chains.
  for (let depth = 0; depth < 8 && cause && !seen.has(cause); depth += 1) {
    seen.add(cause);
    const node =
      typeof cause === 'object'
        ? (cause as { message?: unknown; code?: unknown; cause?: unknown })
        : undefined;
    const message =
      typeof node?.message === 'string' ? node.message : typeof cause === 'string' ? cause : '';
    // This fixed wrapper is owned by api/livekit/createToken, not arbitrary TypeError identity.
    if (operation !== 'auth-access-token' && message === 'Unable to generate the LiveKit token.')
      resolved = 'media-token';
    if (typeof node?.code === 'number' && SAFE_STATUS.has(node.code)) {
      status = node.code;
      transport ||=
        [408, 429, 502, 503, 504].includes(node.code) ||
        (node.code === 500 && !(cause instanceof Error));
      nonTransient ||= [400, 401, 403, 404, 409, 422].includes(node.code);
    }
    nonTransient ||= /\b(401|403|unauthorized|forbidden|permission|decode|validation)\b/i.test(
      message
    );
    transport ||=
      /^(Failed to fetch|NetworkError(?: when attempting to fetch resource\.)?|Load failed|network request failed)$/i.test(
        message
      );
    cause = node?.cause;
  }
  const transient = transport && !nonTransient;
  return {
    copy: transient ? R9_PREVIEW_COPY : R14_PREVIEW_COPY(reference),
    operation: resolved,
    transient,
    status
  };
};

/**
 * R11 copy for a prepared preview that never produced video. Deliberately
 * carries no "you can still continue" promise: the real Continue predicate is
 * the footer's (selection, auth, plan, navigation) and this component cannot
 * observe it.
 */
export const unavailablePreviewCopy = (protocol: string | undefined): string =>
  protocol === 'ndi' ? R11_NDI_COPY : R11_RTSP_COPY;
